import {
  Box,
  Button,
  Flex,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { ReactNode, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { crm } from "../../../theme";

// Blocos visuais compartilhados por Gestão › Operações e Gestão › Câmbio.

// A API manda datas de calendário com "Z": setZone mantém o dia informado (sem converter para o fuso local)
export const fmtDate = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso, { setZone: true }).toFormat("dd/MM/yyyy") : "—";

export const fmtShortDate = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso, { setZone: true }).toFormat("dd/MM") : "—";

export const fmtMoney = (value?: number | null, currency?: string | null) => {
  if (value === undefined || value === null) return "—";
  const code = (currency ?? "").toUpperCase();
  const formatted = new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  if (code === "BRL") return `R$ ${formatted}`;
  return code ? `${code} ${formatted}` : formatted;
};

export const fmtBrl = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(value);

export const fmtUsd = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

export const openProcessPath = (processId: string) => `/process?id=${processId}`;

export const PageHeader = ({ title, subtitle, children }: { title: string; subtitle: string; children?: ReactNode }) => (
  <Flex align="center" justify="space-between" mb="20px" flexWrap="wrap" gap="12px">
    <Flex align="center" gap="12px">
      <Box w="4px" h="32px" bg={crm.brand} borderRadius="full" />
      <Box>
        <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
          {title}
        </Text>
        <Text color={crm.textMuted} fontSize="sm" mt="2px">
          {subtitle}
        </Text>
      </Box>
    </Flex>
    {children}
  </Flex>
);

export const Card = ({
  title,
  right,
  children,
  p = "20px",
}: {
  title?: string;
  right?: ReactNode;
  children: ReactNode;
  p?: string;
}) => (
  <Box bg={crm.surface} borderRadius="16px" p={p} boxShadow={crm.shadowSm} border={`1px solid ${crm.border}`}>
    {(title || right) && (
      <Flex justify="space-between" align="center" mb="14px" gap="8px" flexWrap="wrap">
        {title && (
          <Text fontWeight="700" color={crm.text} fontSize="sm">
            {title}
          </Text>
        )}
        {right}
      </Flex>
    )}
    {children}
  </Box>
);

export type Tone = "default" | "danger" | "warning" | "success";

const toneStyle: Record<Tone, { bg: string; fg: string; border: string }> = {
  default: { bg: crm.brandSoft, fg: crm.brand, border: crm.border },
  danger: { bg: "#fef2f2", fg: crm.danger, border: "#fecaca" },
  warning: { bg: "#fffbeb", fg: crm.warning, border: "#fde68a" },
  success: { bg: "#f0fdf4", fg: crm.success, border: "#bbf7d0" },
};

export const KpiTile = ({
  title,
  value,
  hint,
  icon,
  tone = "default",
  onClick,
}: {
  title: string;
  value: string | number;
  hint?: string;
  icon?: ReactNode;
  tone?: Tone;
  onClick?: () => void;
}) => {
  const t = toneStyle[tone];
  return (
    <Box
      onClick={onClick}
      cursor={onClick ? "pointer" : undefined}
      _hover={onClick ? { borderColor: crm.brand } : undefined}
      bg={crm.surface}
      borderRadius="16px"
      p="18px"
      boxShadow={crm.shadowSm}
      border={`1px solid ${tone === "default" ? crm.border : t.border}`}
    >
      <Flex align="center" gap="10px" mb="10px">
        {icon && (
          <Flex align="center" justify="center" w="34px" h="34px" borderRadius="10px" bg={t.bg} color={t.fg} fontSize="15px" flexShrink={0}>
            {icon}
          </Flex>
        )}
        <Text fontSize="sm" color={crm.textMuted} fontWeight="500">
          {title}
        </Text>
      </Flex>
      <Text fontSize="26px" fontWeight="700" color={tone === "default" ? crm.text : t.fg} lineHeight="1">
        {value}
      </Text>
      {hint && (
        <Text fontSize="xs" color={crm.textFaint} mt="6px">
          {hint}
        </Text>
      )}
    </Box>
  );
};

export const Loading = () => (
  <Flex justify="center" py="48px">
    <Spinner size="lg" color={crm.brand} />
  </Flex>
);

export const EmptyState = ({ children }: { children: ReactNode }) => (
  <Flex justify="center" py="32px">
    <Text color={crm.textFaint} fontSize="sm" textAlign="center">
      {children}
    </Text>
  </Flex>
);

export const ErrorState = ({ onRetry }: { onRetry: () => void }) => (
  <Flex direction="column" align="center" py="40px" gap="12px">
    <Text color={crm.danger} fontSize="sm">
      Não foi possível carregar os dados.
    </Text>
    <Button size="sm" onClick={onRetry} variant="outline" borderColor={crm.border}>
      Tentar novamente
    </Button>
  </Flex>
);

export const filterInputProps = {
  size: "sm" as const,
  borderRadius: "10px",
  borderColor: crm.border,
  bg: crm.surface,
  fontSize: "sm",
  _focus: { borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` },
};

export const SelectFilter = ({
  label,
  value,
  onChange,
  options,
  allLabel = "Todos",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  allLabel?: string;
}) => (
  <Box minW="150px">
    <Text fontSize="11px" fontWeight="600" color={crm.textMuted} mb="3px" textTransform="uppercase">
      {label}
    </Text>
    <Select {...filterInputProps} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  </Box>
);

export const SegmentedTabs = <T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (v: T) => void;
  items: { key: T; label: string; count?: number }[];
}) => (
  <Flex gap="6px" flexWrap="wrap">
    {items.map((item) => {
      const active = item.key === value;
      return (
        <Button
          key={item.key}
          size="sm"
          borderRadius="full"
          fontWeight="500"
          bg={active ? crm.brand : crm.surface}
          color={active ? "white" : crm.text}
          border={`1px solid ${active ? crm.brand : crm.border}`}
          _hover={{ bg: active ? crm.brandDark : crm.brandSoft }}
          onClick={() => onChange(item.key)}
        >
          {item.label}
          {item.count !== undefined && (
            <Text as="span" ml="6px" fontSize="11px" opacity={0.8}>
              {item.count}
            </Text>
          )}
        </Button>
      );
    })}
  </Flex>
);

export interface Column<T> {
  header: string;
  render: (row: T) => ReactNode;
  width?: string;
  isNumeric?: boolean;
}

const thStyle = {
  color: crm.textMuted,
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase" as const,
  bg: crm.subtle,
  borderColor: crm.border,
  py: "10px",
  whiteSpace: "nowrap" as const,
};

const PageControls = ({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
  onPageSize: (s: number) => void;
}) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <Flex justify="space-between" align="center" mt="12px" flexWrap="wrap" gap="8px">
      <Text fontSize="xs" color={crm.textMuted}>
        {from}–{to} de {total}
      </Text>
      <Flex align="center" gap="8px">
        <Select
          {...filterInputProps}
          w="90px"
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
        >
          {[25, 50, 100].map((s) => (
            <option key={s} value={s}>
              {s} / pág.
            </option>
          ))}
        </Select>
        <Button size="sm" variant="outline" borderColor={crm.border} isDisabled={page <= 1} onClick={() => onPage(page - 1)}>
          Anterior
        </Button>
        <Text fontSize="xs" color={crm.textMuted}>
          {page} / {pages}
        </Text>
        <Button size="sm" variant="outline" borderColor={crm.border} isDisabled={page >= pages} onClick={() => onPage(page + 1)}>
          Próxima
        </Button>
      </Flex>
    </Flex>
  );
};

/**
 * Tabela com linhas clicáveis e paginação. Paginação local (rows já completas) por padrão;
 * com `server` a página/total vêm do servidor (controlado pelo pai).
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyText = "Nenhum registro encontrado.",
  server,
  initialPageSize = 25,
  resetKey,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyText?: string;
  server?: { page: number; pageSize: number; total: number; onPage: (p: number) => void; onPageSize: (s: number) => void };
  initialPageSize?: number;
  resetKey?: string;
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    setPage(1);
  }, [resetKey, rows.length]);

  const visible = server ? rows : rows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      <Box overflowX="auto" border={`1px solid ${crm.border}`} borderRadius="12px">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              {columns.map((c) => (
                <Th key={c.header} {...thStyle} isNumeric={c.isNumeric} w={c.width}>
                  {c.header}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {visible.map((row) => (
              <Tr
                key={rowKey(row)}
                cursor={onRowClick ? "pointer" : undefined}
                _hover={onRowClick ? { bg: crm.brandSoft } : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <Td key={c.header} fontSize="13px" color={crm.text} borderColor={crm.border} py="10px" isNumeric={c.isNumeric} verticalAlign="top">
                    {c.render(row)}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
        {visible.length === 0 && <EmptyState>{emptyText}</EmptyState>}
      </Box>
      {server ? (
        <PageControls {...server} onPage={server.onPage} onPageSize={server.onPageSize} />
      ) : (
        rows.length > 0 && (
          <PageControls
            page={page}
            pageSize={pageSize}
            total={rows.length}
            onPage={setPage}
            onPageSize={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        )
      )}
    </>
  );
}

/** Processo + cliente (célula padrão das tabelas). */
export const ProcessCell = ({ ref_, client, urgent }: { ref_: string; client?: string; urgent?: boolean }) => (
  <Box>
    <Flex align="center" gap="6px">
      <Text fontWeight="700" color={crm.brand}>
        {ref_ || "—"}
      </Text>
      {urgent && (
        <Text as="span" fontSize="10px" fontWeight="700" color={crm.danger}>
          URGENTE
        </Text>
      )}
    </Flex>
    <Text fontSize="12px" color={crm.textMuted}>
      {client || "—"}
    </Text>
  </Box>
);
